// ============================================================
//  REPORTS PAGE — ADMIN DASHBOARD
//  Path: src/pages/ReportsPage.tsx
//
//  One report engine, three uses:
//    - Platform-wide (default): transaction volume, platform
//      revenue, expert payouts, new signups — bucketed by
//      day/week/month/year
//    - A specific expert (via ?scope=expert&id=...): their
//      earnings over time
//    - A specific customer (via ?scope=customer&id=...): their
//      spending over time
//
//  Access: super_admin, admin only.
// ============================================================

import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getFinancialReport, getFinancialReportPdf } from '../services/admin.service'
import { FinancialReport, ReportTask } from '../types'

type Period = 'daily' | 'weekly' | 'monthly' | 'yearly'

const ReportsPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const scope = searchParams.get('scope') || 'platform'
  const id    = searchParams.get('id') || undefined

  const [period, setPeriod]   = useState<Period>('monthly')
  const [report, setReport]   = useState<FinancialReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [expandedPeriod, setExpandedPeriod] = useState<string | null>(null)
  // FIX: all three catch blocks on this page were empty. A failed report
  // load rendered "No data available" (reads as "no activity" on a
  // financial report), and a failed PDF did nothing at all.
  const [loadError, setLoadError] = useState('')
  const [pdfError, setPdfError]   = useState('')
  const [pdfBusy, setPdfBusy]     = useState<'view' | 'download' | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    // FIX: switching Daily → Weekly → Monthly quickly could show an
    // earlier period's numbers if its request finished last. A response
    // for a superseded request is now ignored.
    let superseded = false
    const load = async () => {
      setLoading(true)
      setLoadError('')
      try {
        const res = await getFinancialReport({ scope, id, period })
        if (!superseded) setReport(res.data.data)
      } catch (err: any) {
        if (superseded) return
        console.error('Failed to load financial report:', err)
        setReport(null)
        setLoadError(err?.response?.data?.message || (err?.isNetworkError
          ? 'Could not reach the server — check your connection and try again.'
          : 'Failed to load this report.'))
      } finally {
        if (!superseded) setLoading(false)
      }
    }
    load()
    return () => { superseded = true }
  }, [scope, id, period, reloadKey])

  const scopeLabel = scope === 'expert' ? 'Expert' : scope === 'customer' ? 'Customer' : 'Platform'

  // The PDF is requested with responseType 'blob', so an error body
  // arrives as a Blob too — read it to get the server's message.
  const pdfErrorMessage = async (err: any): Promise<string> => {
    if (err?.isNetworkError) return 'Could not reach the server — check your connection and try again.'
    const data = err?.response?.data
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text())
        if (parsed?.message) return parsed.message
      } catch { /* not JSON — fall through */ }
    }
    return 'Could not create the PDF — try again.'
  }

  const fetchPdfUrl = async () => {
    const res = await getFinancialReportPdf({ scope, id, period })
    return URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
  }

  const handleViewPdf = async () => {
    setPdfError('')
    // FIX: the tab is opened NOW, inside the click, and filled in once the
    // PDF arrives. Opening it after the await got it blocked as a pop-up
    // (always in Safari; in Chrome once the PDF took a few seconds).
    const tab = window.open('', '_blank')
    setPdfBusy('view')
    try {
      const blobUrl = await fetchPdfUrl()
      if (tab) {
        tab.location.href = blobUrl
      } else {
        window.location.assign(blobUrl) // pop-ups fully disabled: open here instead
      }
      // Freed after the viewer has had time to load it.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch (err) {
      console.error('Failed to open report PDF:', err)
      tab?.close()
      setPdfError(await pdfErrorMessage(err))
    } finally {
      setPdfBusy(null)
    }
  }

  const handleDownloadPdf = async () => {
    setPdfError('')
    setPdfBusy('download')
    try {
      const blobUrl = await fetchPdfUrl()
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `financial-report-${scope}-${period}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      // FIX: revoking immediately after click() can cancel the download
      // in Firefox and Safari — give the browser time to start it.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000)
    } catch (err) {
      console.error('Failed to download report PDF:', err)
      setPdfError(await pdfErrorMessage(err))
    } finally {
      setPdfBusy(null)
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Financial Reports</h1>
        <p className="text-gray-400 text-sm mt-1">{scopeLabel}{id ? ` (${id.slice(0, 8)}...)` : ''} — all figures for the selected period</p>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          {(['daily', 'weekly', 'monthly', 'yearly'] as Period[]).map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-4 py-2 text-sm font-medium capitalize rounded-xl transition-colors ${
                period === p ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-gray-400 hover:text-white'
              }`}>{p}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={handleViewPdf} disabled={pdfBusy !== null}
            className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light disabled:opacity-50 transition-colors">
            {pdfBusy === 'view' ? 'Preparing…' : '📄 View PDF'}
          </button>
          <button onClick={handleDownloadPdf} disabled={pdfBusy !== null}
            className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors">
            {pdfBusy === 'download' ? 'Preparing…' : '⬇ Download PDF'}
          </button>
        </div>
      </div>

      {pdfError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {pdfError}
          <button onClick={() => setPdfError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : loadError ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{loadError}</p>
          <button onClick={() => setReloadKey((k) => k + 1)}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      ) : !report ? (
        <div className="text-center py-8 text-gray-400">No data available</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-surface-card border border-border-light rounded-2xl p-5">
              <p className="text-2xl font-bold text-white">${report.totals.transactionVolume.toFixed(2)}</p>
              <p className="text-sm text-gray-400 mt-1">{scope === 'platform' ? 'Transaction Volume' : scope === 'customer' ? 'Total Spent' : 'Total Job Value'}</p>
            </div>
            {scope === 'platform' && (
              <div className="bg-surface-card border border-border-light rounded-2xl p-5">
                <p className="text-2xl font-bold text-white">${report.totals.platformFee.toFixed(2)}</p>
                <p className="text-sm text-gray-400 mt-1">Platform Revenue</p>
              </div>
            )}
            {scope !== 'customer' && (
              <div className="bg-surface-card border border-border-light rounded-2xl p-5">
                <p className="text-2xl font-bold text-white">${report.totals.expertPayout.toFixed(2)}</p>
                <p className="text-sm text-gray-400 mt-1">{scope === 'expert' ? 'Total Earned' : 'Expert Payouts'}</p>
              </div>
            )}
            <div className="bg-surface-card border border-border-light rounded-2xl p-5">
              <p className="text-2xl font-bold text-white">{report.totals.taskCount}</p>
              <p className="text-sm text-gray-400 mt-1">Jobs</p>
            </div>
            {scope === 'platform' && (
              <div className="bg-surface-card border border-border-light rounded-2xl p-5">
                <p className="text-2xl font-bold text-white">{report.totals.newUsers}</p>
                <p className="text-sm text-gray-400 mt-1">New Signups</p>
              </div>
            )}
          </div>

          <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border-light"><h2 className="text-lg font-semibold text-white capitalize">{period} Breakdown</h2></div>
            {report.rows.length === 0 ? (
              <p className="text-center py-8 text-gray-400">No activity in this period range</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-light">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Period</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Jobs</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">
                      {scope === 'platform' ? 'Transaction Volume' : scope === 'customer' ? 'Spent' : 'Job Value'}
                    </th>
                    {scope === 'platform' && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Platform Revenue</th>}
                    {scope !== 'customer' && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">{scope === 'expert' ? 'Earned' : 'Expert Payouts'}</th>}
                    {scope === 'platform' && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">New Signups</th>}
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <React.Fragment key={r.period}>
                      <tr onClick={() => setExpandedPeriod(expandedPeriod === r.period ? null : r.period!)}
                        className="border-b border-border-light hover:bg-surface-tertiary/50 transition-colors cursor-pointer">
                        <td className="px-5 py-4 text-sm text-white">
                          {expandedPeriod === r.period ? '▼' : '▶'} {r.period}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-300">{r.taskCount}</td>
                        <td className="px-5 py-4 text-sm text-green-400">${r.transactionVolume.toFixed(2)}</td>
                        {scope === 'platform' && <td className="px-5 py-4 text-sm text-brand-400">${r.platformFee.toFixed(2)}</td>}
                        {scope !== 'customer' && <td className="px-5 py-4 text-sm text-purple-400">${r.expertPayout.toFixed(2)}</td>}
                        {scope === 'platform' && <td className="px-5 py-4 text-sm text-gray-300">{r.newUsers}</td>}
                      </tr>
                      {expandedPeriod === r.period && r.tasks && (
                        <tr>
                          <td colSpan={6} className="px-5 py-4 bg-surface-tertiary/30">
                            <div className="space-y-2">
                              {r.tasks.map((t: ReportTask) => (
                                <div key={t.taskId} className="bg-surface-card border border-border-light rounded-xl p-3">
                                  <div className="flex justify-between items-start mb-1">
                                    <p className="text-sm font-medium text-white">{t.title}</p>
                                    <p className="text-sm text-green-400">${(t.totalCharged || 0).toFixed(2)}</p>
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    Date: {t.completedAt ? new Date(t.completedAt).toLocaleDateString() : t.hiredAt ? new Date(t.hiredAt).toLocaleDateString() : '—'}
                                    {' · '}Location: {t.area || t.address || '—'}
                                  </p>
                                  {scope === 'platform' && (
                                    <p className="text-xs text-gray-500">
                                      {t.customerName ? `Customer: ${t.customerName}` : ''}{t.expertName ? `  ·  Expert: ${t.expertName}` : ''}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-500">
                                    Platform Fee: ${t.platformFee.toFixed(2)} · Expert Payout: ${t.expertPayout.toFixed(2)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default ReportsPage